import { DenormalizedCustomizableGroup, DenormalizedCustomizableQuestion, GroupType } from '@packages/types'
import classNames from 'classnames'
import React, { useCallback, useEffect } from 'react'

import { addItem } from 'customizer/bulkOrder/actions'
import {
  bulkOrderDenormalizedGroupSelector,
  isBulkOrderTotalQuantityEqualOrMoreThanMaxSelector,
} from 'customizer/bulkOrder/selectors'
import * as customizationSelectors from 'customizer/customization/selectors'
import { stepIsAvailable } from 'customizer/customization/utils'
import { useDispatch, useSelector } from 'customizer/hooks'
import * as questionPanelActions from 'customizer/questionPanel/actions'
import { setShowFinishCustomization, setMobileDescription, addVisitedQuestions } from 'customizer/questionPanel/actions'
import { mobileDescriptionSelector, showFinishCustomizationSelector } from 'customizer/questionPanel/selectors'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import { getDisabledColor } from 'customizer/theme/utils'
import AddIcon from 'icons/bold/01-Interface Essential/43-Remove-Add/add.svg'
import BulkOrderBottomDrawer from 'themes/common/components/bulkOrder/bottomDrawer/BulkOrderBottomDrawer'
import BulkOrderBottomDrawerFooter from 'themes/common/components/bulkOrder/bottomDrawer/BulkOrderBottomDrawerFooter'
import BulkOrderBottomDrawerTitle from 'themes/common/components/bulkOrder/bottomDrawer/BulkOrderBottomDrawerTitle'
import BulkOrderEmptyQuantity from 'themes/common/components/bulkOrder/BulkOrderEmptyQuantity'
import BulkOrderFormActionButton from 'themes/common/components/bulkOrder/form/BulkOrderFormActionButton'
import BulkOrderFormActions from 'themes/common/components/bulkOrder/form/BulkOrderFormActions'

import { stepsSelector, selectedStepIdSelector, invalidStepsSelector } from '../../selectors'
import ActionSection from './ActionSection'
import DescriptionButton from './DescriptionButton'
import MobileDescriptionPopup from './MobileDescriptionPopup'
import Step from './Step'
import TitleNavigation from './TitleNavigation'

import './QuestionPanel.scss'
import './bulkOrder/BulkOrderBottomDrawer.scss'

const { isReadySelector } = customizationSelectors
const { selectStep } = questionPanelActions

type StepType = DenormalizedCustomizableGroup | DenormalizedCustomizableQuestion

const QuestionPanel = () => {
  const dispatch = useDispatch()
  const steps: StepType[] = useSelector(stepsSelector)
  const requiredQuestions = useSelector(invalidStepsSelector)
  const selectedStepId = useSelector(selectedStepIdSelector)
  const isReady = useSelector(isReadySelector)
  const bulkOrderGroup = useSelector(bulkOrderDenormalizedGroupSelector)
  const isSelectedStepBulkOrder = bulkOrderGroup && selectedStepId === bulkOrderGroup.id
  const showFinishCustomization = useSelector(showFinishCustomizationSelector)

  const isDisabled = useSelector(isBulkOrderTotalQuantityEqualOrMoreThanMaxSelector)
  const mobileDescription = useSelector(mobileDescriptionSelector)
  const { backgroundColor, borderColor, borderWidth, linkColor } = useThemeSettings('questionPanel')
  const { backgroundColor: finishCustomizationBackgroundColor } = useThemeSettings('customizer')
  const descriptionStyle = useThemeSettings('description')
  const actionSectionStyle = useThemeSettings('actionSection')
  const bulkOrderStyle = useThemeSettings('bulkOrder')

  const setSelectedStepId = useCallback((stepId: string) => dispatch(selectStep(stepId)), [dispatch])

  const isLastStepSelected = steps[steps.length - 1]?.id === selectedStepId

  const handleSelectNextStep = (): void => {
    dispatch(setMobileDescription(null))

    if (isLastStepSelected) {
      dispatch(setShowFinishCustomization(isLastStepSelected))
      return
    }

    const currentStepIndex = steps.findIndex(step => step.id === selectedStepId)

    setSelectedStepId(steps[currentStepIndex + 1].id)
  }

  const handleSelectPreviousStep = () => {
    const currentStepIndex = steps.findIndex(step => step.id === selectedStepId)

    setSelectedStepId(steps[currentStepIndex - 1].id)
    dispatch(setMobileDescription(null))
  }

  const handleSelectLastStep = () => {
    setSelectedStepId(steps[steps.length - 1].id)
    dispatch(setShowFinishCustomization(false))
  }

  useEffect(() => {
    dispatch(addVisitedQuestions(selectedStepId))
  }, [selectedStepId])

  const currentStep = steps.find(step => step.id === selectedStepId)

  const showStepContent = currentStep != null && !showFinishCustomization

  const showBulkOrderBottomDrawer = isSelectedStepBulkOrder && showStepContent && stepIsAvailable(bulkOrderGroup)

  const transformStepName = (steps: StepType[]): StepType[] => {
    return steps.map(step => {
      if (step.type === GroupType.BulkOrder && step.children.length === 0 && step.quantity.name) {
        return { ...step, name: step.quantity.name }
      }

      return step
    })
  }

  if (!isReady) return null

  return (
    <div
      className={classNames('question-panel-section', {
        'question-panel-section--last-step': showFinishCustomization,
      })}
      style={{ backgroundColor: showBulkOrderBottomDrawer ? finishCustomizationBackgroundColor : undefined }}
    >
      {showStepContent && (
        <div
          className={classNames('description__mobile-container', {
            'bulk-order-description': currentStep.type === GroupType.BulkOrder,
          })}
        >
          <MobileDescriptionPopup
            key={`question-description-${currentStep.id}`}
            className="description option-description"
            aria-label="question description"
            descriptionStyle={{
              ...descriptionStyle,
              fontFamily: descriptionStyle?.fontFamily?.fontFamily,
              color: descriptionStyle?.color,
              backgroundColor: descriptionStyle.backgroundColor,
              fontSize: descriptionStyle.fontSize,
              borderRadius: descriptionStyle.rounding,
              paddingTop: 0,
              linkColor,
            }}
          />
        </div>
      )}

      {showBulkOrderBottomDrawer && (
        <BulkOrderBottomDrawer
          bulkOrderGroup={bulkOrderGroup}
          titleClassName="bulk-order-bottom-drawer__title"
          collapsedHeight={184}
          headerHeight={42}
          title={
            <div className="bulk-order-bottom-drawer__description-title-container">
              <div
                className={classNames({
                  'bulk-order-bottom-drawer__description-title': !mobileDescription,
                })}
              >
                <BulkOrderBottomDrawerTitle />
              </div>
              <DescriptionButton
                isVisible={mobileDescription}
                onClick={() => dispatch(setMobileDescription(currentStep))}
                style={{ marginLeft: '4px' }}
              />
            </div>
          }
          footer={
            <BulkOrderBottomDrawerFooter bulkOrderGroup={bulkOrderGroup} style={{ backgroundColor }}>
              <ActionSection
                steps={steps}
                selectedStepId={selectedStepId}
                requiredQuestions={requiredQuestions}
                selectPreviousStep={handleSelectPreviousStep}
                selectNextStep={handleSelectNextStep}
                selectLastStep={handleSelectLastStep}
                isFinishingCustomization={showFinishCustomization}
              />
            </BulkOrderBottomDrawerFooter>
          }
        >
          <BulkOrderFormActions>
            <BulkOrderFormActionButton
              onClick={() => dispatch(addItem(bulkOrderGroup))}
              style={{
                color: isDisabled ? getDisabledColor(bulkOrderStyle.buttonTextColor) : bulkOrderStyle.buttonTextColor,
                fill: isDisabled ? getDisabledColor(bulkOrderStyle.buttonTextColor) : bulkOrderStyle.buttonTextColor,
                borderColor: isDisabled
                  ? getDisabledColor(bulkOrderStyle.buttonBorderColor)
                  : bulkOrderStyle.buttonBorderColor,
                borderWidth: bulkOrderStyle.buttonBorderThickness,
                borderRadius: actionSectionStyle.buttonsBorderRadius,
                fontFamily: actionSectionStyle.fontFamily.fontFamily,
                fontSize: actionSectionStyle.fontSize,
              }}
              disabled={isDisabled}
            >
              <AddIcon
                style={{
                  width: `calc(${actionSectionStyle.fontSize} / 1.5)`,
                  height: `calc(${actionSectionStyle.fontSize} / 1.5`,
                  borderColor: isDisabled
                    ? getDisabledColor(bulkOrderStyle.buttonBorderColor)
                    : bulkOrderStyle.buttonBorderColor,
                  borderWidth: bulkOrderStyle.buttonBorderThickness,
                  borderRadius: actionSectionStyle.buttonsBorderRadius,
                }}
              />
              {bulkOrderGroup.addItemLabel && (
                <span className="bulk-order-form__actions-button--label">{bulkOrderGroup.addItemLabel}</span>
              )}
            </BulkOrderFormActionButton>
          </BulkOrderFormActions>
        </BulkOrderBottomDrawer>
      )}

      <div
        className="mobile-question-panel"
        style={{
          backgroundColor: showFinishCustomization ? finishCustomizationBackgroundColor : backgroundColor,
          borderColor,
          borderWidth,
          visibility: showBulkOrderBottomDrawer ? 'hidden' : undefined,
        }}
      >
        {showStepContent && (
          <TitleNavigation step={transformStepName(steps).find(s => s.id === selectedStepId)} steps={steps} />
        )}
        {isSelectedStepBulkOrder && !showFinishCustomization && (
          <div className="step step--mobile empty-quantity-input">
            <BulkOrderEmptyQuantity hiddenTitle bulkOrderGroup={bulkOrderGroup} />
          </div>
        )}

        {showStepContent && !isSelectedStepBulkOrder && <Step step={currentStep as DenormalizedCustomizableQuestion} />}

        <ActionSection
          steps={steps}
          requiredQuestions={requiredQuestions}
          selectedStepId={selectedStepId}
          selectPreviousStep={handleSelectPreviousStep}
          selectNextStep={handleSelectNextStep}
          selectLastStep={handleSelectLastStep}
          isFinishingCustomization={showFinishCustomization}
        />
      </div>
    </div>
  )
}

export default QuestionPanel
